import { Component, effect, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TaskDetailsComponent } from './components/task-details/task-details.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TaskDetail, Task } from '../../models/task.model';
import { Subject, takeUntil } from 'rxjs';
import { TasksHistoryComponent } from './components/tasks-history/tasks-history.component';
import { TasksTabComponent } from './components/tasks-tab/tasks-tab.component';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { TaskService } from '@olv/ui-shared/services/task.service';

@Component({
  selector: 'app-tasks',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    TasksTabComponent,
    TasksHistoryComponent,
    TaskDetailsComponent,
    HasPermissionDirective,
  ],
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TasksComponent implements OnInit {
  private merchantService = inject(MerchantService);
  private taskService = inject(TaskService);
  private router = inject(Router);

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  sideComponentOpen = false;
  updateTick = 0;
  activeTab: 'Tasks' | 'History' = 'Tasks';
  selectedTask: Task | null = null;
  selectedTaskDetail: TaskDetail | null = null;
  targetTaskHistoryId: string | null = null;
  targetTaskId: string | null = null;
  tasksSearchText = '';
  historySearchText = '';
  private route = inject(ActivatedRoute);
  private destroy$ = new Subject<void>();

  constructor() {
    effect(() => {
      this.sideComponentOpen = this.merchantService.sidebarOpenSignal();
    });

    this.activeTab = this.route.snapshot.data['tab'] ?? 'Tasks';
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((qp) => {
      const taskId = qp.get('taskId');
      const search = qp.get('search');
      const legacyActiveTab = qp.get('activeTab');

      if (this.activeTab === 'Tasks' && legacyActiveTab === 'History') {
        this.router.navigate(['/tasks', 'history'], {
          queryParams: {
            ...(taskId ? { taskId } : {}),
            ...(search ? { search } : {}),
          },
          replaceUrl: true,
        });
        return;
      }

      if (!taskId && !search && !legacyActiveTab) {
        return;
      }

      this.targetTaskId = null;
      this.targetTaskHistoryId = null;
      this.tasksSearchText = '';
      this.historySearchText = '';

      if (this.activeTab === 'History') {
        this.targetTaskHistoryId = taskId ?? search;
        this.historySearchText = search ?? taskId ?? '';
      } else if (taskId) {
        this.targetTaskId = taskId;
      } else if (search) {
        this.tasksSearchText = search;
      }
      this.clearQueryParams();
    });
  }
  onTaskSelected(task: Task): void {
    this.targetTaskId = null;
    this.selectedTask = task;
    this.selectedTaskDetail = null; // Reset detail while loading

    // Load full task details
    this.taskService.getTaskById(task.id).subscribe({
      next: (fullTask) => {
        if (this.selectedTask?.id !== task.id) {
          return;
        }

        this.selectedTask.delegationDetail = fullTask.delegationDetail;

        // Extract task detail based on task type
        if (fullTask.whitelistedWalletDetail) {
          this.selectedTaskDetail = fullTask.whitelistedWalletDetail;
        } else if (fullTask.merchantChangeDetail) {
          this.selectedTaskDetail = fullTask.merchantChangeDetail;
        } else if (fullTask.riskAlertDetail) {
          this.selectedTaskDetail = fullTask.riskAlertDetail;
        } else if (fullTask.transactionDetail) {
          // spread so every transaction field reaches the drawer, task fields win
          this.selectedTask.transactionDetail = fullTask.transactionDetail;
          this.selectedTaskDetail = {
            ...fullTask.transactionDetail,
            createdAt: new Date(fullTask.createdAt),
            resolvedByDisplayName: fullTask.resolvedByDisplayName,
            resolvedAt: fullTask.resolvedAt,
            resolution: fullTask.resolution,
          };
        }
      },
      error: (error) => {
        console.error('Error loading task details:', error);
        // Still open sidebar even if details fail to load
      },
    });

    this.merchantService.openSidebar();
  }

  onTasksLoaded(tasks: Task[]): void {
    // Auto-select task if navigated from notification
    if (this.targetTaskId && tasks.length > 0) {
      const targetTask = tasks.find((task) => task.id === this.targetTaskId);
      if (targetTask) {
        this.onTaskSelected(targetTask);
        this.targetTaskId = null; // Clear to avoid re-selection
      }
    }
  }

  closeTaskDetails(): void {
    this.selectedTask = null;
    this.merchantService.closeSidebar();
  }

  onTaskUpdated(): void {
    this.updateTick++;
  }

  onHistoryTaskSelected(task: Task): void {
    this.closeTaskDetails();
    this.onTaskSelected(task);
  }
  get sidebarOpen() {
    return this.merchantService.sidebarOpenSignal();
  }
  private clearQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      preserveFragment: true,
      replaceUrl: true,
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
